import * as z from "zod";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../../providers/form";
import InputField from "widgets/shared/inputs/inputField";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import GenericButton from "widgets/controls/buttons/generic";
import { useEffect } from "react";
// Schema
const GasConfigurationSchema = z.object({
  dailyUsageLimit: z.any(),
});

export type GasConfigurationFormType = z.infer<
  typeof GasConfigurationSchema
>;

// Props
type GasConfigurationFormProps = {
  submit: (data: GasConfigurationFormType) => void;
  onClose: () => void;
  defaultValues?: GasConfigurationFormType;
  values?: GasConfigurationFormType;
  loading?: boolean;
  disabled?: boolean;
};

const GasConfigurationForm = ({
  submit,
  onClose,
  defaultValues,
  values,
  loading,
  disabled,
}: GasConfigurationFormProps) => {
  const form = useForm<GasConfigurationFormType>({
    resolver: zodResolver(GasConfigurationSchema),
    defaultValues: {
      dailyUsageLimit: 0,
    },
    values,
  });

  const { t } = useTranslation();
  const { errors, isDirty } = form.formState;

  const clearErrors = () => {
    form.clearErrors();
  };

  // Automatically submit the form if valid
  const onSubmit = async (formData: GasConfigurationFormType) => {
    try {
      await submit(formData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  useEffect(() => {
    if (disabled && isDirty) {
      form.reset(values || defaultValues);
    }
  }, [disabled, isDirty, form, values, defaultValues]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className=" space-y-2">
        <div className="grid grid-cols-1 gap-2">
          <FormField
            control={form.control}
            name="dailyUsageLimit"
            rules={{ required: false }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("form.input.daily.limit", { context: "gas" })}
                </FormLabel>
                <FormControl>
                  <InputField
                    sizes={"md"}
                    placeholder={t("form.placeholder.daily.limit", {
                      context: "gas",
                    })}
                    id="daily-usage-limit"
                    type="number"
                    state={errors.dailyUsageLimit ? "error" : undefined}
                    onFocus={clearErrors}
                    color="secondary"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end py-2">
          <div className="flex gap-2">
            <GenericButton
              onClick={onClose}
              text={t("button.action.cancel")}
              size="md"
              variant="ghost"
              color="secondary"
            />

            <GenericButton
              type="submit"
              text={t("button.action.save")}
              loading={loading || false}
              size="md"
            />
          </div>
        </div>
      </form>
    </Form>
  );
};

export default GasConfigurationForm;
